import { Button, TextInput, Tooltip as LegoTooltip } from 'lego-on-react';
import React from 'react';

import block from 'propmods';

import { ApiError, sendEmail } from '../../../api';
import { Loading } from '../../Loading/Loading';
import { emailDescription } from '../../Settings/channels/descriptions';
import { Field } from '../Form';

import './EmailField.scss';

const b = block('EmailField');

type DisplayTooltipOption = 'success' | 'overuse' | 'failure';

interface TooltipProps {
    buttonRef: React.RefObject<Button>;
    displayTooltip?: DisplayTooltipOption;
    onOutsideClick?: () => void;
}

const Tooltip: React.SFC<TooltipProps> = ({ buttonRef, displayTooltip, onOutsideClick }) => {
    const labels: Record<DisplayTooltipOption, string> = {
        success: 'Отправлено',
        failure: 'Произошла ошибка',
        overuse: 'Попробуйте через 10 секунд',
    };

    const themes: Record<DisplayTooltipOption, 'white' | 'error'> = {
        success: 'white',
        failure: 'error',
        overuse: 'error',
    };

    if (!displayTooltip) {
        return null;
    }

    return (
        <LegoTooltip
            visible={Boolean(displayTooltip)}
            anchor={buttonRef.current!}
            theme={themes[displayTooltip]}
            autoclosable
            onOutsideClick={onOutsideClick}
            to="right"
            size="s"
        >
            {labels[displayTooltip]}
        </LegoTooltip>
    );
};

interface Props {
    label: string;
    error?: string;
    verificationError?: string;
    value: string;
    onChange: (text: string) => void;
    disabled?: boolean;
    isEmailSent: boolean;
    skillId: string;
}

interface State {
    clicked: boolean;
    isSending: boolean;
    displayTooltip?: DisplayTooltipOption;
}

class EmailField extends React.Component<Props, State> {
    public state: State = {
        clicked: false,
        isSending: false,
    };

    private buttonRef = React.createRef<Button>();

    public render() {
        const { label, error, value, verificationError, onChange, disabled, isEmailSent } = this.props;
        const { clicked, isSending } = this.state;

        return (
            <div {...b()}>
                <Field name="email" label={label} error={error} verificationError={verificationError}>
                    <TextInput
                        theme="normal"
                        // В хроме type=email автоматом переводит unicode в punycode, потому type=text
                        // https://justmarkup.com/log/2015/02/input-type-email-better-dont-use-it - в начале краткое описание проблемы
                        type="text"
                        size="s"
                        text={value}
                        onChange={onChange}
                        disabled={disabled}
                        autocomplete={false}
                    />
                    {emailDescription(isEmailSent)}
                    {isEmailSent ? (
                        <>
                            <Button
                                name="resend"
                                theme="normal"
                                size="s"
                                ref={this.buttonRef}
                                disabled={clicked}
                                onClick={this.onResendClick}
                            >
                                Отправить снова
                                {isSending ? (
                                    <div {...b('loading')}>
                                        <Loading size="xs" centered />
                                    </div>
                                ) : null}
                            </Button>
                            <Tooltip
                                buttonRef={this.buttonRef}
                                onOutsideClick={this.onOutsideClick}
                                displayTooltip={this.state.displayTooltip}
                            />
                        </>
                    ) : null}
                </Field>
            </div>
        );
    }

    private onResendClick = async () => {
        const { skillId, value: email } = this.props;

        this.setState({
            clicked: true,
            isSending: true,
        });

        try {
            await sendEmail({
                userId: __appConfig.user.blackbox.uid!,
                skillId,
                email,
            });

            this.setState(() => ({
                isSending: false,
                displayTooltip: 'success',
            }));
        } catch (e) {
            if (e instanceof ApiError && e.code === 429) {
                this.setState(() => ({
                    isSending: false,
                    displayTooltip: 'overuse',
                }));

                return;
            }

            this.setState(() => ({
                isSending: false,
                displayTooltip: 'failure',
            }));
        }
    };

    private onOutsideClick = () => this.setState({ displayTooltip: undefined });
}

export default EmailField;
